import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/lib/brand";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";
import { BackButton } from "@/components/BackButton";

const BG = "https://images.unsplash.com/photo-1446038202205-1c96430dbdab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYXBlJTIwdmVyZGUlMjBvY2VhbiUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODcyMjM2NjF8MA&ixlib=rb-4.1.0&q=85";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="flex min-h-screen">
      <div className="relative hidden w-1/2 lg:block">
        <img src={BG} alt="Cabo Verde" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-petrol/95 via-petrol/50 to-petrol/20" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <div className="mb-5">
            <Link to="/" aria-label="GESTA CV — início"><Logo onDark className="h-12 w-auto" testid="auth-logo-white" /></Link>
          </div>
          <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight">
            Gestão simples para PME <br /> de Cabo Verde
          </h2>
          <p className="mt-3 max-w-md text-sm text-white/70">
            Clientes, orçamentos, vendas, caixa e despesas num só lugar. Moeda em escudos (CVE).
          </p>
        </div>
      </div>
      <div className="flex w-full items-center justify-center px-6 py-10 lg:w-1/2">
        <div className="w-full max-w-sm">
          <BackButton fallback="/" className="-ml-3 mb-4" testid="auth-back-btn" />
          <div className="mb-8 flex items-center lg:hidden">
            <Link to="/" aria-label="GESTA CV — início"><Logo className="h-14 w-auto" testid="auth-logo-color" /></Link>
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">{title}</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Entrar" subtitle="Aceda à sua conta para gerir o seu negócio.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">E-mail</Label>
          <Input id="email" type="email" required value={email} data-testid="login-email"
            onChange={(e) => setEmail(e.target.value)} placeholder="nome@empresa.cv" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Palavra-passe</Label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">
              Esqueceu-se?
            </Link>
          </div>
          <PasswordInput id="password" required value={password} testid="login-password"
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="login-submit">
          {busy ? <CircleNotch className="animate-spin" /> : "Entrar"}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Não tem conta?{" "}
          <Link to="/register" className="font-medium text-primary hover:underline" data-testid="register-link">
            Registar empresa
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
